/** Settings → Account: email address, password, plan, signing out. */
import { useState } from "react";
import { toast } from "sonner";
import { CircleCheck, LoaderCircle, LogOut, MailWarning } from "lucide-react";
import { errorMessage, patch, post } from "@/lib/api";
import { qk, useMe } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlanSettings } from "./PlanSettings";

export async function signOut() {
  await post("/api/auth/logout", {}).catch(() => undefined);
  window.location.assign("/");
}

export function AccountSettings() {
  const me = useMe();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const u = me.data;
  if (!u) return null;

  return (
    <div className="space-y-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1">
          Signed in as <strong>{u.displayName}</strong>
          {u.email && <> · {u.email}</>}
          {u.isDemo && <span className="text-muted-foreground"> (demo user, local development)</span>}
        </span>
        <Button size="sm" variant="outline" onClick={() => void signOut()}>
          <LogOut /> Sign out
        </Button>
      </div>

      {!u.isDemo && u.email && (
        <p className="flex flex-wrap items-center gap-2">
          {u.emailVerified ? (
            <>
              <CircleCheck className="size-4 text-success" aria-hidden /> Your email address is confirmed.
            </>
          ) : (
            <>
              <MailWarning className="size-4 text-warning" aria-hidden /> Your email address is not confirmed yet. Open the link we sent you.
              <Button
                size="xs"
                variant="outline"
                disabled={busy === "verify"}
                onClick={async () => {
                  setBusy("verify");
                  try {
                    await post("/api/me/resend-verification", {});
                    toast.success(`We sent a new link to ${u.email}.`);
                  } catch (e) {
                    toast.error(errorMessage(e));
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                Send it again
              </Button>
            </>
          )}
        </p>
      )}

      <PlanSettings />

      {!u.isDemo && (
        <label className="flex items-start gap-2">
          <Checkbox
            className="mt-0.5"
            checked={u.announcements !== false}
            onCheckedChange={async (v) => {
              try {
                await patch("/api/me", { announcements: v === true });
                await qc.invalidateQueries({ queryKey: qk.me });
                toast.success(v === true ? "You will receive news and offers." : "You will not receive news and offers any more.");
              } catch (e) {
                toast.error(errorMessage(e));
              }
            }}
          />
          <span>
            News and offers from BrainLog by email (a few times a year at most)
            <span className="block text-xs text-muted-foreground">Account emails (signing in, security, payments) always arrive.</span>
          </span>
        </label>
      )}

      {u.hasPassword && (
        <form
          className="space-y-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy("password");
            try {
              await post("/api/me/password", { current, password });
              setCurrent("");
              setPassword("");
              toast.success("Password changed. Other browsers were signed out.");
            } catch (err) {
              toast.error(errorMessage(err));
            } finally {
              setBusy(null);
            }
          }}
        >
          <p className="font-medium">Change your password</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="pw-current">Current password</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-56" required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-new">New password</Label>
              <Input id="pw-new" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-56" required />
            </div>
            <Button type="submit" size="sm" disabled={busy === "password" || password.length < 8}>
              {busy === "password" && <LoaderCircle className="animate-spin" />} Change password
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">At least 8 characters. Every other browser where you are signed in is signed out.</p>
        </form>
      )}
    </div>
  );
}
